/**
 * 산정 API 의 400 처리 (aivatar #488 ②)
 *
 * `cancellation-refund` · `rollback-stay` · `rollback-settlement` 세 곳은 400 을 **실패가 아니라
 * "산정할 수 없다 + 이유" 라는 정상 응답**으로 낸다. 서버가 재료가 어긋난 상태를 값으로 내지 않고
 * 끊기 때문이고, 그때 body 의 `reason` 이 곧 담당자에게 줄 조치 안내다.
 *
 * ```
 * "청약철회 기간이 지나 되감기 대상이 아니다 — 원상회복 없이 구독취소 규정(13.3)을 적용한다"
 * "현재 활성 약정이 아니라 되감을 수 없다 — 연쇄 전환은 마지막 전환부터 역순으로 되돌린다"
 * "기준월 지급 원장 행을 찾을 수 없다"
 * ```
 *
 * 🔴 `reason` 을 요약하거나 자체 문구로 갈아끼우지 말 것. 다음에 무엇을 해야 하는지가 문장 안에
 * 들어 있어서, 줄이면 그게 사라진다.
 *
 * 되감기 버튼은 이제 조건부로 숨긴다 — 다만 판정하는 쪽은 여전히 서버다. 자격 6조건을 복제하는
 * 대신 `InvoiceRefundView.rollbackAvailable` 불리언 하나를 읽는다(aipark-front #269). 그 값이
 * 참인데도 여기서 400 이 오는 경우(기준일을 바꿔 다시 부른 산정 등)는 남으므로, 이 유틸의
 * 몫은 그대로다 — 받은 이유를 그대로 띄운다.
 */

export type QuoteResult<T> = { ok: true; data: T } | { ok: false; reason: string }

/** 산정을 끊은 이유를 찾을 수 없을 때. 이 문구가 보이면 서버 계약이 바뀐 것이다 */
const UNKNOWN_REASON = '산정할 수 없습니다. 사유가 응답에 없어 서버 로그 확인이 필요합니다.'

const reasonOf = (payload: unknown): string | null => {
  if (!payload || typeof payload !== 'object') return null
  const { reason } = payload as { reason?: unknown }
  return typeof reason === 'string' && reason.trim() ? reason : null
}

/**
 * 산정 호출을 `{ ok }` 로 감싼다.
 *
 * 200 인데 `reason` 이 실려 오는 경우도 실패로 본다 — 서버가 `unavailable()` 로 낸 응답은
 * 금액 필드가 전부 null 이라 그대로 그리면 "환불액 -" 만 남아 이유를 못 읽는다.
 *
 * 400 이 아닌 오류(401·404·500·네트워크)는 **다시 던진다.** 여기서 삼키면 "산정 불가" 와
 * "서버가 죽었다" 가 화면에서 같아 보이고, 담당자는 없는 정책 문제를 찾게 된다.
 */
export const quoteOrReason = async <T>(call: () => Promise<T>): Promise<QuoteResult<T>> => {
  try {
    const data = await call()
    const reason = reasonOf(data)
    return reason ? { ok: false, reason } : { ok: true, data }
  } catch (e: any) {
    if (e?.response?.status !== 400) throw e
    return { ok: false, reason: reasonOf(e.response?.data) ?? UNKNOWN_REASON }
  }
}
